import { Target, Award, ShieldCheck, Briefcase, Globe, Compass, ArrowUpRight } from 'lucide-react';

export default function About() {
  return (
    <section id="nosotros" className="py-32 bg-white relative overflow-hidden mesh-bg">
      {/* Decorative Blur Blobs */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-accent/5 rounded-full blur-[120px] -z-10 translate-x-1/3 -translate-y-1/3" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-primary/5 rounded-full blur-[100px] -z-10 -translate-x-1/4 translate-y-1/4" />

      <div className="section-container relative z-10">
        {/* Quiénes Somos Section */}
        <div className="grid lg:grid-cols-12 gap-16 items-center mb-40">
          <div className="lg:col-span-6" data-aos="fade-right">
            <div className="badge-premium mb-8">
              <Compass className="w-4 h-4 text-accent" />
              <span>Trayectoria y Visión</span>
            </div>
            <h2 className="text-4xl md:text-6xl font-black text-primary-dark mb-10 leading-[1.1] tracking-tighter">
              Liderando la <br />
              <span className="gradient-text">Revolución Energética</span>
            </h2>
            <div className="space-y-8 text-lg text-slate-600 leading-relaxed font-light">
              <p>
                CIAC Energy SpA nace como respuesta a la necesidad de una <span className="font-bold text-primary-dark underline decoration-accent/30 underline-offset-4">industria más inteligente</span>,
                donde la eficiencia no sea una opción, sino el motor del crecimiento.
              </p>
              <p>
                No solo instalamos equipos; diseñamos ecosistemas de ahorro. Mediante la electrificación térmica y la automatización avanzada,
                permitimos que las empresas reduzcan su huella ambiental mientras optimizan su rentabilidad operativa.
              </p>
            </div>

            <div className="flex flex-wrap gap-4 mt-12">
              <div className="glass-card px-6 py-4 rounded-2xl flex items-center gap-4 bg-white/50 border-white/40">
                <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center text-accent">
                  <Award className="w-6 h-6" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest font-bold text-slate-400">Garantía</div>
                  <div className="font-bold text-primary-dark">Sello SEC Clase A</div>
                </div>
              </div>
              <div className="glass-card px-6 py-4 rounded-2xl flex items-center gap-4 bg-white/50 border-white/40">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
                  <Briefcase className="w-6 h-6" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest font-bold text-slate-400">Expertise</div>
                  <div className="font-bold text-primary-dark">+15 Años de Experiencia</div>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-6 relative" data-aos="fade-left">
            <div className="relative grid grid-cols-2 gap-6">
              <div className="space-y-6">
                <div className="h-72 rounded-[2.5rem] bg-primary relative overflow-hidden group shadow-2xl hover:-translate-y-2 transition-transform duration-500">
                  <div className="absolute inset-0 bg-gradient-to-t from-primary-dark to-transparent opacity-60 z-10" />
                  <img
                    src={`${import.meta.env.BASE_URL}images/about_corporate_team_light.png`}
                    alt="Equipo CIAC Group"
                    className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700"
                  />
                  <div className="absolute bottom-6 left-6 z-20">
                    <div className="text-3xl font-black text-white">100%</div>
                    <div className="text-[10px] uppercase tracking-widest font-bold text-white/70">Capital Chileno</div>
                  </div>
                </div>
                <div className="h-48 rounded-[2rem] bg-accent p-8 flex flex-col justify-between text-white shadow-xl hover:-translate-y-2 transition-transform duration-500">
                  <Globe className="w-10 h-10 opacity-50" />
                  <div className="text-lg font-bold">Proyección Regional</div>
                </div>
              </div>
              <div className="space-y-6 pt-12">
                <div className="h-48 rounded-[2rem] bg-slate-900 flex items-center justify-center text-white shadow-xl hover:-translate-y-2 transition-transform duration-500">
                  <div className="text-center">
                    <div className="text-5xl font-black text-accent">+500</div>
                    <div className="text-[10px] uppercase tracking-widest font-bold text-white/50">Proyectos</div>
                  </div>
                </div>
                <div className="h-72 rounded-[2.5rem] bg-energy-blue relative overflow-hidden group shadow-2xl hover:-translate-y-2 transition-transform duration-500">
                  <div className="absolute inset-0 bg-primary/20 z-10" />
                  <div className="relative z-20 p-8 h-full flex flex-col justify-end text-white">
                    <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center mb-4">
                      <ArrowUpRight className="w-5 h-5" />
                    </div>
                    <div className="text-xl font-black leading-tight">Continuidad Operacional Garantizada</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Background element */}
            <div className="absolute -z-10 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] border border-slate-100 rounded-full pointer-events-none opacity-50" />
          </div>
        </div>

        {/* Misión - Alternative Visuals */}
        <div className="grid lg:grid-cols-12 gap-16 items-center">
          <div className="lg:col-span-5 order-2 lg:order-1" data-aos="zoom-in">
            <div className="relative group">
              <div className="absolute -inset-4 bg-gradient-to-tr from-accent to-primary opacity-20 blur-2xl group-hover:opacity-30 transition-opacity" />
              <div className="relative p-12 bg-slate-900 rounded-[3rem] text-white overflow-hidden">
                <div className="absolute top-0 right-0 w-40 h-40 bg-accent/20 rounded-full blur-3xl -mr-20 -mt-20" />
                <Target className="w-14 h-14 text-accent mb-10" />
                <h3 className="text-3xl font-display font-medium leading-relaxed italic mb-10">
                  "Nuestro propósito es <span className="text-accent italic font-bold">electrificar</span> el mañana de forma sostenible, convirtiendo procesos críticos en energía limpia y eficiente."
                </h3>
                <div className="flex items-center gap-6">
                  <div className="h-px flex-1 bg-white/10" />
                  <span className="text-[10px] uppercase tracking-[0.3em] font-bold text-slate-500">Misión Corporativa</span>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7 order-1 lg:order-2" data-aos="fade-left">
            <div className="badge-premium mb-8 bg-energy-blue/10 text-energy-blue border-energy-blue/20">
              <ShieldCheck className="w-4 h-4" />
              <span>Compromiso del Grupo</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-primary-dark mb-8 leading-tight tracking-tighter">
              Transformación con <br />
              <span className="text-slate-400">Impacto Real</span>
            </h2>
            <p className="text-xl text-slate-600 mb-10 leading-relaxed font-light">
              Trabajamos cada día para fomentar el progreso social y un crecimiento circular. Nuestra misión guía cada proyecto, asegurando que la tecnología no solo sea eficiente, sino resiliente y accesible.
            </p>
            <div className="grid sm:grid-cols-3 gap-6">
              {[
                { title: 'Descarbonización', desc: 'Eliminación del diésel industrial.' },
                { title: 'IA Térmica', desc: 'Algoritmos de optimización real.' },
                { title: 'Triple Impacto', desc: 'Social, Ambiental y Económico.' }
              ].map((item, i) => (
                <div key={i} className="p-6 rounded-2xl bg-slate-50 border border-slate-100 hover:bg-white hover:shadow-xl transition-all duration-300">
                  <div className="w-2 h-10 bg-accent/30 rounded-full mb-4" />
                  <div className="font-bold text-primary-dark mb-1">{item.title}</div>
                  <div className="text-xs text-slate-500">{item.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}


